import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getOrders,
  deleteAdmin,
  updateOrder,
  uploadReceipts,
  setPayableOrder,
  downloadReceipt,
} from "../../utils/backend";
import {
  CSpinner,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CCard,
  CCardBody,
  CCardHeader,
  CForm,
  CFormInput,
  CRow,
  CCol,
  CFormLabel,
  CButton,
  CCardFooter,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormSelect,
  CAccordion,
  CAccordionItem,
  CAccordionHeader,
  CAccordionButton,
  CAccordionBody,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
} from "@coreui/react-pro";

import { CPagination, CPaginationItem } from "@coreui/react-pro";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const OrdersProduct = () => {
  const [orders, setorders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [totalPayableAmount, setTotalPayableAmount] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [idFilter, setIdFilter] = useState(""); //
  const [usernameFilter, setUsernameFilter] = useState(""); //
  const [fullNameFilter, setFullNameFilter] = useState(""); //
  const [emailFilter, setEmailFilter] = useState(""); //
  const [insurancePlanFilter, setInsurancePlanFilter] = useState(""); //
  const [insurerNameFilter, setInsurerNameFilter] = useState(""); //
  const [insuranceTypeFilter, setInsuranceTypeFilter] = useState(""); //
  const [orderNumberFilter, setOrderNumberFilter] = useState(""); //
  const [orderStatusFilter, setOrderStatusFilter] = useState(""); //
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(""); //

  const hasPermission = (permissionGroup, permissionName) => {
    let permissions;
    try {
      permissions = JSON.parse(localStorage.getItem("permissions")) || [];
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      permissions = [];
    }

    return permissions.some(
      (perm) =>
        perm.permission_group === permissionGroup &&
        perm.permission_name === permissionName
    );
  };

  useEffect(() => {
    // Load filters and pagination state from localStorage on page load
    const savedState = JSON.parse(localStorage.getItem("ordersProductState"));
    if (savedState) {
      setIdFilter(savedState.idFilter || "");
      setUsernameFilter(savedState.usernameFilter || "");
      setFullNameFilter(savedState.fullNameFilter || "");
      setEmailFilter(savedState.emailFilter || "");
      setInsurancePlanFilter(savedState.insurancePlanFilter || "");
      setInsurerNameFilter(savedState.insurerNameFilter || "");
      setInsuranceTypeFilter(savedState.insuranceTypeFilter || "");
      setOrderNumberFilter(savedState.orderNumberFilter || "");
      setOrderStatusFilter(savedState.orderStatusFilter || "");
      setPaymentStatusFilter(savedState.paymentStatusFilter || "");
      setStartDate(savedState.startDate || "");
      setEndDate(savedState.endDate || "");
      setPerPage(savedState.perPage || 10);

      // Fetch data based on the saved state
      // Always land on page 1. Restoring a saved page number stranded admins on
      // stale rows: the list is sorted newest-first, so page N>1 never shows new
      // rows and nothing on screen explained why.
      const filters = {
        id: savedState.idFilter || "",
        username: savedState.usernameFilter || "",
        full_name: savedState.fullNameFilter || "",
        email: savedState.emailFilter || "",
        insurance_plan: savedState.insurancePlanFilter || "",
        insurer_name: savedState.insurerNameFilter || "",
        insurance_type: savedState.insuranceTypeFilter || "",
        order_number: savedState.orderNumberFilter || "",
        order_status: savedState.orderStatusFilter || "",
        payment_status: savedState.paymentStatusFilter || "",
        start_date: savedState.startDate || "",
        end_date: savedState.endDate || "",
        page: 1,
        perPage: savedState.perPage || 10,
        order_type: "product",
      };
      fetchData(filters);
    } else {
      fetchData();
    }
  }, []);

  const saveStateToLocalStorage = () => {
    const state = {
      idFilter,
      usernameFilter,
      fullNameFilter,
      emailFilter,
      insurancePlanFilter,
      insurerNameFilter,
      insuranceTypeFilter,
      orderNumberFilter,
      orderStatusFilter,
      paymentStatusFilter,
      startDate,
      endDate,
    };
    localStorage.setItem("ordersProductState", JSON.stringify(state));
  };

  const fetchData = async (
    filters = { perPage: perPage, order_type: "product" }
  ) => {
    setIsLoading(true);
    try {
      const response = await getOrders(filters);
      setorders(response.data.data);
      setCurrentPage(response.data.currentPage);
      setTotalPages(response.data.totalPages);
      setTotalItems(response.data.totalItems);
    } catch (error) {
      console.error("Error fetching data:", error);
      setorders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const filters = {
      id: idFilter, //
      username: usernameFilter, //
      full_name: fullNameFilter, //
      email: emailFilter, //
      insurance_plan: insurancePlanFilter, //
      insurer_name: insurerNameFilter, //
      insurance_type: insuranceTypeFilter, //
      order_number: orderNumberFilter, //
      order_status: orderStatusFilter, //
      payment_status: paymentStatusFilter, //
      start_date: startDate,
      end_date: endDate,
      page: 1,
      perPage: perPage,
      order_type: "product",
    };
    saveStateToLocalStorage(); // Save state before fetching data
    await fetchData(filters);
  };

  const handleReset = () => {
    setIdFilter("");
    setUsernameFilter("");
    setFullNameFilter("");
    setEmailFilter("");
    setInsurancePlanFilter("");
    setInsurerNameFilter("");
    setInsuranceTypeFilter("");
    setOrderNumberFilter("");
    setOrderStatusFilter("");
    setPaymentStatusFilter("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);

    // Retain only perPage in localStorage
    const existingState =
      JSON.parse(localStorage.getItem("ordersProductState")) || {};
    localStorage.setItem(
      "ordersProductState",
      JSON.stringify({ perPage: existingState.perPage || 10 })
    );

    fetchData();
  };

  const handlePageChange = async (page) => {
    const filters = {
      id: idFilter, //
      username: usernameFilter, //
      full_name: fullNameFilter, //
      email: emailFilter, //
      insurance_plan: insurancePlanFilter, //
      insurer_name: insurerNameFilter, //
      insurance_type: insuranceTypeFilter, //
      order_number: orderNumberFilter, //
      order_status: orderStatusFilter, //
      payment_status: paymentStatusFilter, //
      start_date: startDate,
      end_date: endDate,
      page: page,
      perPage: perPage,
      order_type: "product",
    };
    await fetchData(filters);
  };

  const handlePerPageChange = async (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1); // Reset to the first page
    localStorage.setItem(
      "ordersProductState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("ordersProductState")),
        perPage: newPerPage,
      })
    );
    const filters = {
      id: idFilter, //
      username: usernameFilter, //
      full_name: fullNameFilter, //
      email: emailFilter, //
      insurance_plan: insurancePlanFilter, //
      insurer_name: insurerNameFilter, //
      insurance_type: insuranceTypeFilter, //
      order_number: orderNumberFilter, //
      order_status: orderStatusFilter, //
      payment_status: paymentStatusFilter, //
      start_date: startDate,
      end_date: endDate,
      page: 1,
      perPage: newPerPage,
      order_type: "product",
    };
    await fetchData(filters);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAdmin({ id });
      toast.success("order deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.message);
      console.error("Failed to delete order:", error);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const updatedStatus =
      newStatus.charAt(0).toUpperCase() + newStatus.slice(1);

    const originalOrder = orders.find((order) => order.id === orderId);
    const originalStatus = originalOrder ? originalOrder.order_status : null;

    if (originalStatus === updatedStatus) {
      return;
    }

    if (newStatus === "Paid" || newStatus === "Completed") {
      setSelectedOrderId(orderId);
      setShowModal(true);
      return;
    }

    setorders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, order_status: updatedStatus } : order
      )
    );

    try {
      const response = await updateOrder({
        id: orderId,
        order_status: newStatus,
      });
      if (response.success) {
        toast.success("Order status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");
      setorders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? { ...order, order_status: originalStatus }
            : order
        )
      );
    }
  };

  const handlePaymentStatusChange = async (orderId, newStatus) => {
    const updatedStatus =
      newStatus.charAt(0).toUpperCase() + newStatus.slice(1);

    const originalOrder = orders.find((order) => order.id === orderId);
    const originalStatus = originalOrder ? originalOrder.payment_status : null;
    if (originalStatus === updatedStatus) {
      return;
    }
    setorders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId
          ? { ...order, payment_status: updatedStatus }
          : order
      )
    );

    try {
      const response = await updateOrder({
        id: orderId,
        payment_status: newStatus,
      });
      if (response.success) {
        toast.success("Payment status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");

      setorders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? { ...order, payment_status: originalStatus }
            : order
        )
      );
    }
  };

  const handleFileChange = (e) => {
    setReceiptFile(e.target.files[0]);
  };

  const handleUploadReceipt = async () => {
    if (!receiptFile || !selectedOrderId || !totalPayableAmount) {
      toast.error("Please select a file and enter the total payable amount");
      return;
    }

    const formData = new FormData();
    formData.append("order_id", selectedOrderId);
    formData.append("file", receiptFile);

    try {
      const response = await uploadReceipts(formData);
      if (response.success) {
        toast.success("Receipt uploaded successfully");
        setShowModal(false);
        setReceiptFile(null);
        setTotalPayableAmount("");
        await updateOrder({
          id: selectedOrderId,
          order_status: "Paid",
        });
        toast.success("Order status updated successfully");

        await setPayableOrder({
          order_id: selectedOrderId,
          total_payable_amount: totalPayableAmount,
        });

        fetchData();
      } else {
        throw new Error(response.message || "Failed to upload receipt");
      }
    } catch (error) {
      console.error("Error uploading receipt:", error);
      toast.error("Error uploading receipt");
      setorders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === selectedOrderId
            ? { ...order, order_status: "Pending Payment" }
            : order
        )
      );
    }
  };

  const handleDownloadReceipt = async (orderId) => {
    try {
      const response = await downloadReceipt({ order_id: orderId });
      if (response.success) {
        if (response.data.receipt) {
          const link = document.createElement("a");
          link.href = response.data.receipt;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success("Receipt downloaded successfully");
        } else {
          toast.error("Receipt not found");
        }
      } else {
        throw new Error(response.message || "Failed to download receipt");
      }
    } catch (error) {
      console.error("Error downloading receipt:", error);
      toast.error("Error downloading receipt");
    }
  };

  const insuranceTypeMapping = {
    3: "Health Insurance",
    4: "Personal Accident",
  };

  const generatePerPageOptions = (totalItems) => {
    if (totalItems <= 10) {
      return [10];
    }
    const options = [];
    let interval = 10;
    while (interval < totalItems) {
      options.push(interval);
      if (interval >= 50) {
        interval += 50;
      } else {
        interval *= 5;
      }
    }
    const nearestHigherMultipleOf50 = Math.ceil(totalItems / 50) * 50;
    options.push(nearestHigherMultipleOf50);
    return options;
  };

  // Surfaced next to Reset so an admin can never be silently filtered down to a subset.
  const activeFilterCount = [
    idFilter,
    usernameFilter,
    fullNameFilter,
    emailFilter,
    insurancePlanFilter,
    insurerNameFilter,
    insuranceTypeFilter,
    orderNumberFilter,
    orderStatusFilter,
    paymentStatusFilter,
    startDate,
    endDate,
  ].filter(function (v) {
    return v !== "" && v !== null && v !== undefined;
  }).length;

  return (
    <>
      <CCard className="mb-4 border-light">
        <CAccordion alwaysOpen activeItemKey={1}>
          <CAccordionItem itemKey={1}>
            <CAccordionHeader>Search Filters</CAccordionHeader>
            <CAccordionBody>
              <CCardBody>
                <CForm onSubmit={handleSearch}>
                  <CRow>
                    {/* <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="id">ID </CFormLabel>
                      <CFormInput
                        type="text"
                        id="id"
                        placeholder="Search by ID"
                        value={idFilter}
                        onChange={(e) => setIdFilter(e.target.value)}
                      />
                    </CCol> */}
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="username">Username </CFormLabel>
                      <CFormInput
                        type="text"
                        id="username"
                        placeholder="Search by Username"
                        value={usernameFilter}
                        onChange={(e) => setUsernameFilter(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="full_name">Full Name </CFormLabel>
                      <CFormInput
                        type="text"
                        id="full_name"
                        placeholder="Search by Full Name"
                        value={fullNameFilter}
                        onChange={(e) => setFullNameFilter(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="email">Email </CFormLabel>
                      <CFormInput
                        type="text"
                        id="email"
                        placeholder="Search by Email"
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="insurance_plan">
                        Insurance Plan{" "}
                      </CFormLabel>
                      <CFormInput
                        type="text"
                        id="insurance_plan"
                        placeholder="Search by Insurance Plan"
                        value={insurancePlanFilter}
                        onChange={(e) => setInsurancePlanFilter(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="insurer_name">
                        Insurer Name{" "}
                      </CFormLabel>
                      <CFormInput
                        type="text"
                        id="insurer_name"
                        placeholder="Search by Insurer Name"
                        value={insurerNameFilter}
                        onChange={(e) => setInsurerNameFilter(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="insurance_type">
                        Insurance Type{" "}
                      </CFormLabel>
                      <CFormSelect
                        id="insurance_type"
                        value={insuranceTypeFilter}
                        onChange={(e) => setInsuranceTypeFilter(e.target.value)}
                      >
                        <option value="">Select Insurance Type</option>
                        {Object.entries(insuranceTypeMapping).map(
                          ([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          )
                        )}
                      </CFormSelect>
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="order_number">
                        Order Number{" "}
                      </CFormLabel>
                      <CFormInput
                        type="text"
                        id="order_number"
                        placeholder="Search by Order Number"
                        value={orderNumberFilter}
                        onChange={(e) => setOrderNumberFilter(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="order_status">
                        Order Status{" "}
                      </CFormLabel>
                      <CFormSelect
                        id="order_status"
                        value={orderStatusFilter}
                        onChange={(e) => setOrderStatusFilter(e.target.value)}
                      >
                        <option value="">Select Order Status</option>
                        <option value="Pending Quotation">
                          Pending Quotation
                        </option>
                        <option value="Quotation is Ready">
                          Quotation is Ready
                        </option>
                        <option value="Pending Payment">Pending Payment</option>
                        <option value="Payment Success">Payment Success</option>
                        <option value="Active">Active</option>
                        <option value="Expired & Renewal Required">
                          Expired & Renewal Required
                        </option>
                        <option value="Paid">Paid</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </CFormSelect>
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="payment_status">
                        Payment Status{" "}
                      </CFormLabel>
                      <CFormSelect
                        id="payment_status"
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      >
                        <option value="">Select Payment Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Completed">Completed</option>
                      </CFormSelect>
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="start_date">
                        Created At (From)
                      </CFormLabel>
                      <CFormInput
                        type="date"
                        id="start_date"
                        placeholder="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </CCol>
                    <CCol xs="12" md="4" className="mb-3">
                      <CFormLabel htmlFor="end_date">
                        Created At (To)
                      </CFormLabel>
                      <CFormInput
                        type="date"
                        id="end_date"
                        placeholder="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </CCol>
                  </CRow>
                  <CRow>
                    <CCol className="justify-content-end d-flex my-1 align-items-center">
                      {activeFilterCount > 0 && (
                        <span
                          className="me-3 text-nowrap"
                          style={{
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffe69c",
                            borderRadius: "12px",
                            color: "#664d03",
                            fontSize: "0.8125rem",
                            fontWeight: 600,
                            padding: "4px 10px",
                          }}
                        >
                          <i className="bi bi-funnel-fill me-1"></i>
                          {activeFilterCount}{" "}
                          {activeFilterCount === 1 ? "filter" : "filters"} active —
                          showing a subset
                        </span>
                      )}
                      <CButton
                        className="ansa-button-secondary me-3"
                        type="button"
                        onClick={handleReset}
                      >
                        Reset
                      </CButton>
                      <CButton className="ansa-button-primary" type="submit">
                        Search
                      </CButton>
                    </CCol>
                  </CRow>
                </CForm>
              </CCardBody>
            </CAccordionBody>
          </CAccordionItem>
        </CAccordion>
      </CCard>
      <CCard className="border-light">
        <CCardBody style={{ overflowX: "auto" }}>
          {isLoading ? (
            <div>
              <Skeleton height={40} count={1} />
              <Skeleton height={30} count={5} style={{ marginTop: "10px" }} />
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <CTable borderless striped>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>No.</CTableHeaderCell>
                    <CTableHeaderCell>Username</CTableHeaderCell>
                    <CTableHeaderCell>Full Name</CTableHeaderCell>
                    <CTableHeaderCell>Email</CTableHeaderCell>
                    <CTableHeaderCell>Insurance Plan</CTableHeaderCell>
                    <CTableHeaderCell>Insurer Name</CTableHeaderCell>
                    <CTableHeaderCell>Insurance Type</CTableHeaderCell>
                    <CTableHeaderCell>Order Number</CTableHeaderCell>
                    <CTableHeaderCell>Order Status</CTableHeaderCell>
                    <CTableHeaderCell>Payment Status</CTableHeaderCell>
                    <CTableHeaderCell>Created At</CTableHeaderCell>
                    <CTableHeaderCell>Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {orders.map((order, index) => (
                    <CTableRow key={order.id}>
                      <CTableDataCell>
                        {index + 1 + perPage * (currentPage - 1)}
                      </CTableDataCell>
                      <CTableDataCell>{order.username || "N/A"}</CTableDataCell>
                      <CTableDataCell>
                        {order.full_name ||
                          (order.order_details &&
                            order.order_details.full_name) ||
                          "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {(order.user_details && order.user_details.email) ||
                          "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {order.insurance_plan || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {order.insurer_name || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {(order.order_details &&
                          insuranceTypeMapping[
                            order.order_details.insurance_type
                          ]) ||
                          "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        {order.order_number || "N/A"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CDropdown>
                          <CDropdownToggle
                            color="secondary"
                            variant="outline"
                            disabled={
                              !hasPermission("Order Management", "Edit")
                            }
                            style={{
                              backgroundColor:
                                order.order_status === "Pending Payment"
                                  ? "#8B0000"
                                  : order.order_status === "Paid"
                                  ? "#66BB6A"
                                  : order.order_status === "In Progress"
                                  ? "#A9A9A9"
                                  : order.order_status === "Completed"
                                  ? "#006400"
                                  : "#A9A9A9",
                              color: "#fff",
                              border: "none",
                              width: "160px",
                              fontSize: "14px",
                              borderRadius: "20px",
                              fontWeight: "500",
                            }}
                          >
                            {order.order_status
                              ? order.order_status.charAt(0).toUpperCase() +
                                order.order_status.slice(1)
                              : "N/A"}
                          </CDropdownToggle>
                          <CDropdownMenu>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(
                                  order.id,
                                  "Pending Quotation"
                                )
                              }
                            >
                              Pending Quotation
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(
                                  order.id,
                                  "Quotation is Ready"
                                )
                              }
                            >
                              Quotation is Ready
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(order.id, "Pending Payment")
                              }
                            >
                              Pending Payment
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(order.id, "Payment Success")
                              }
                            >
                              Payment Success
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(order.id, "Active")
                              }
                            >
                              Active
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(
                                  order.id,
                                  "Expired & Renewal Required"
                                )
                              }
                            >
                              Expired & Renewal Required
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(order.id, "Paid")
                              }
                            >
                              Paid
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(order.id, "In Progress")
                              }
                            >
                              In Progress
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handleStatusChange(order.id, "Completed")
                              }
                            >
                              Completed
                            </CDropdownItem>
                          </CDropdownMenu>
                        </CDropdown>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CDropdown>
                          <CDropdownToggle
                            color="secondary"
                            variant="outline"
                            disabled={
                              !hasPermission("Order Management", "Edit")
                            }
                            style={{
                              backgroundColor:
                                order.payment_status === "Pending"
                                  ? "#A9A9A9"
                                  : order.payment_status === "Completed"
                                  ? "#66BB6A"
                                  : "#A9A9A9",
                              color: "#fff",
                              border: "none",
                              width: "160px",
                              fontSize: "14px",
                              borderRadius: "20px",
                              fontWeight: "500",
                            }}
                          >
                            {order.payment_status
                              ? order.payment_status.charAt(0).toUpperCase() +
                                order.payment_status.slice(1)
                              : "N/A"}
                          </CDropdownToggle>
                          <CDropdownMenu>
                            <CDropdownItem
                              onClick={() =>
                                handlePaymentStatusChange(order.id, "Pending")
                              }
                            >
                              Pending
                            </CDropdownItem>
                            <CDropdownItem
                              onClick={() =>
                                handlePaymentStatusChange(order.id, "Completed")
                              }
                            >
                              Completed
                            </CDropdownItem>
                          </CDropdownMenu>
                        </CDropdown>
                      </CTableDataCell>
                      <CTableDataCell style={{ minWidth: "110px" }}>
                        {order.created_at
                          ? new Date(order.created_at).toLocaleString()
                          : "N/A"}
                      </CTableDataCell>{" "}
                      <CTableDataCell className="d-flex align-items-center">
                        {hasPermission("Order Management", "View") && (
                          <CButton
                            onClick={() =>
                              navigate(`/orders/product/details/${order.id}`)
                            }
                          >
                            <i className="me-2 bi bi-eye-fill"></i>
                          </CButton>
                        )}
                        {(order.order_status === "Paid" ||
                          order.order_status === "Completed") && (
                          <CButton
                            onClick={() => handleDownloadReceipt(order.id)}
                          >
                            <i className="me-2 bi bi-download"></i>
                          </CButton>
                        )}
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
          )}

          {!isLoading && (
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <CPagination
                  className="mx-2"
                  aria-label="Page navigation example"
                  onActivePageChange={async (page) => {
                    setCurrentPage(page);
                    await fetchData(
                      {
                        username,
                        fullName,
                        email,
                        startDate,
                        endDate,
                        perPage: perPage,
                      },
                      page
                    );
                  }}
                  activePage={currentPage}
                  pages={totalPages}
                  doubleArrows={false}
                  arrows
                >
                  <CPaginationItem
                    aria-label="Previous"
                    disabled={currentPage <= 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    <span aria-hidden="true">&laquo;</span>
                  </CPaginationItem>
                  {[...Array(totalPages).keys()].map((pageNumber) => {
                    if (
                      pageNumber === 0 ||
                      pageNumber === totalPages - 1 ||
                      pageNumber < 5 ||
                      (pageNumber >= currentPage - 2 &&
                        pageNumber <= currentPage + 2)
                    ) {
                      return (
                        <CPaginationItem
                          key={pageNumber + 1}
                          active={currentPage === pageNumber + 1}
                          onClick={() => handlePageChange(pageNumber + 1)}
                        >
                          {pageNumber + 1}
                        </CPaginationItem>
                      );
                    } else if (
                      (pageNumber === 5 && currentPage > 7) ||
                      (pageNumber === currentPage + 3 &&
                        currentPage < totalPages - 4)
                    ) {
                      return (
                        <CPaginationItem
                          key={`ellipsis-${pageNumber}`}
                          disabled
                        >
                          ...
                        </CPaginationItem>
                      );
                    } else {
                      return null;
                    }
                  })}
                  <CPaginationItem
                    aria-label="Next"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    <span aria-hidden="true">&raquo;</span>
                  </CPaginationItem>
                </CPagination>
              </div>
              <div className="d-flex align-items-center">
                <span className="me-3">
                  Showing {orders.length} of {totalItems} orders
                </span>
                <CFormSelect
                  className="w-auto"
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                >
                  {generatePerPageOptions(totalItems).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal visible={showModal} onClose={() => setShowModal(false)}>
        <CModalHeader>
          <CModalTitle>Please fill in the following fields</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CFormLabel htmlFor="receiptFile">Upload Receipt</CFormLabel>
          <CFormInput
            type="file"
            id="receiptFile"
            onChange={handleFileChange}
          />
          <CFormLabel htmlFor="totalPayableAmount" className="mt-3">
            Total Payable Amount
          </CFormLabel>
          <CFormInput
            type="number"
            id="totalPayableAmount"
            value={totalPayableAmount}
            onChange={(e) => setTotalPayableAmount(e.target.value)}
          />
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </CButton>
          <CButton color="primary" onClick={handleUploadReceipt}>
            Submit
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

export default OrdersProduct;
